<template>
  <div class="manage-menu">
    <header class="manage-menu__header">
      <div class="manage-menu__header-inner">
        <h2 class="manage-menu__title">Manage Menu</h2>
        <div class="manage-menu__header-actions">
          <a
            :href="siteUrl"
            target="_blank"
            rel="noopener"
            class="manage-menu__btn-secondary"
          >
            View Site
          </a>
          <button type="button" class="manage-menu__btn-primary" @click="goToAdd">
            <span class="material-symbols-outlined">add</span>
            Add menu item
          </button>
        </div>
      </div>
    </header>

    <div class="manage-menu__body">
      <div class="manage-menu__list">
        <menu-tree
          :items="menuItems"
          :parent="null"
          :depth="0"
          :expanded-ids="expandedIds"
          :handlers="treeHandlers"
        />
      </div>
    </div>

    <footer v-if="unsavedCount > 0" class="manage-menu__footer">
      <div class="manage-menu__footer-status">
        <span class="manage-menu__footer-dot" aria-hidden="true" />
        <span>{{ unsavedCount }} unsaved modification{{ unsavedCount !== 1 ? 's' : '' }}</span>
      </div>
      <div class="manage-menu__footer-actions">
        <button type="button" class="manage-menu__footer-discard" @click="discardChanges">
          Discard changes
        </button>
        <button type="button" class="manage-menu__footer-publish" @click="publishMenu">
          Publish Menu
        </button>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { Message } from 'element-ui'
import MenuTree from '@/components/MenuTree'

export default {
  name: 'Dashboard',

  components: {
    MenuTree
  },

  data () {
    return {
      dragSource: null
    }
  },

  created () {
    this.$store.dispatch('menu/fetchMenu')
  },

  computed: {
    ...mapState('menu', ['menuItems', 'expandedIds']),
    ...mapGetters('menu', ['unsavedCount']),

    siteUrl () {
      if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_LOCAL_FRONTEND) {
        return process.env.VUE_APP_LOCAL_FRONTEND.replace(/\/$/, '')
      }
      return typeof window !== 'undefined' ? 'https://mondial90.rs' : '#'
    },

    treeHandlers () {
      return {
        canEdit: (item) => this.canEditItem(item),
        canDelete: (item) => !this.isProtectedMenuItem(item),
        toggleExpand: (id) => this.toggleExpand(id),
        edit: (item) => this.goToEdit(item),
        addSub: (item) => this.goToAddSubItem(item),
        remove: (parent, index) => this.deleteItem(parent, index),
        isDragging: (parent, index) => this.dragSource != null &&
          this.dragSource.parent === parent && this.dragSource.index === index,
        dragStart: (e, parent, index) => this.onDragStart(e, parent, index),
        dragEnd: () => this.onDragEnd(),
        dragOver: (e) => this.onDragOver(e),
        drop: (parent, index) => this.onDrop(parent, index)
      }
    }
  },

  methods: {
    protectedMenuTitles () {
      return [
        'O nama', 'About Us', 'About',
        'Projekti', 'Projects',
        'Galerija', 'Gallery',
        'Kontakt', 'Contact',
        'Home'
      ]
    },

    isProtectedMenuItem (item) {
      if (!item) return false
      const normalized = (v) => (v && String(v).trim().toLowerCase()) || ''
      const set = new Set(this.protectedMenuTitles().map((t) => t.trim().toLowerCase()))
      const label = normalized(item.label)
      const srb = normalized(item.labelSrb)
      const eng = normalized(item.labelEng)
      return set.has(label) || set.has(srb) || set.has(eng)
    },

    canEditItem (item) {
      return !this.isProtectedMenuItem(item)
    },

    goToAdd () {
      this.$router.push({ name: 'ManageMenuAdd' })
    },

    goToAddSubItem (parent) {
      this.$router.push({ name: 'ManageMenuAdd', query: { parentId: parent.id } })
    },

    goToEdit (item) {
      this.$router.push({ name: 'ManageMenuEdit', params: { id: item.id } })
    },

    deleteItem (parent, index) {
      if (parent) {
        this.removeMenuItem({ parent, childIndex: index })
      } else {
        this.removeMenuItem({ topLevelIndex: index })
      }
    },

    toggleExpand (id) {
      this.$store.commit('menu/toggleExpand', id)
    },

    discardChanges () {
      this.$store.commit('menu/discardChanges')
    },

    async publishMenu () {
      try {
        await this.$store.dispatch('menu/saveMenu')
        Message.success('Meni je uspešno sačuvan.')
      } catch (_) {
        Message.error('Greška pri čuvanju menija. Proverite konekciju i API.')
      }
    },

    ...mapMutations('menu', ['removeMenuItem']),

    onDragStart (e, parent, index) {
      this.dragSource = { parent, index }
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
    },

    onDragEnd () {
      this.dragSource = null
    },

    onDragOver (e) {
      if (!this.dragSource) return
      e.dataTransfer.dropEffect = 'move'
    },

    onDrop (parent, index) {
      const source = this.dragSource
      this.onDragEnd()
      // Dragging only reorders within one list; moving an item to another
      // level is done through the Edit form's "Parent menu" field.
      if (!source || source.parent !== parent) return
      this.$store.commit('menu/moveMenuItem', {
        parent,
        fromIndex: source.index,
        toIndex: index
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.manage-menu {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: 'Manrope', sans-serif;
}

.manage-menu__header {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(24px);
  padding: 2rem 3rem;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
}

.manage-menu__header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.manage-menu__title {
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0;
}

.manage-menu__header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.manage-menu__btn-secondary {
  padding: 0.75rem 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  background: #fff;
  text-decoration: none;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  &:hover {
    background: #f8fafc;
    color: #0f172a;
    border-color: #e2e8f0;
  }
}

.manage-menu__btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #1a1a1a;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  .material-symbols-outlined {
    font-size: 0.875rem;
  }
  &:hover {
    background: #334155;
  }
}

.manage-menu__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 2rem 3rem;
}

.manage-menu__list {
  background: #fff;
  border-radius: 2rem;
  border: 1px solid #f1f5f9;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.manage-menu__footer {
  flex-shrink: 0;
  padding: 1rem 3rem 2rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  background: #fff;
}

.manage-menu__footer-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
}

.manage-menu__footer-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #eab308;
  flex-shrink: 0;
}

.manage-menu__footer-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.manage-menu__footer-discard {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem 0;
  &:hover {
    color: #0f172a;
  }
}

.manage-menu__footer-publish {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  background: #1a1a1a;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #334155;
  }
}
</style>
